/**
 * The persistence seam: a tiny string key/value store that features read and
 * write through instead of touching `localStorage` directly. v1 backs it with the browser's
 * localStorage; a future account backend swaps the implementation without touching features,
 * and tests run against the in-memory fake. Values are strings - callers JSON-encode via the
 * readJSON / writeJSON helpers below.
 */
export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

/**
 * The namespace + schema version every persisted key carries. There is no migration in v1
 * (nothing has ever been persisted); a breaking schema change bumps this version, which orphans
 * every old blob so each feature falls back to its default. Each feature names its own keys.
 */
export const STORAGE_VERSION = "v1";

/** Build a namespaced, versioned storage key, e.g. storageKey("tweaks") -> "keylens:v1:tweaks". */
export function storageKey(name: string): string {
  return `keylens:${STORAGE_VERSION}:${name}`;
}

/** An in-memory store: the test fake, and the fallback when the browser denies localStorage. */
export function memoryStore(seed?: Record<string, string>): KeyValueStore {
  const map = new Map<string, string>(seed ? Object.entries(seed) : undefined);
  return {
    get: (key) => (map.has(key) ? (map.get(key) as string) : null),
    set: (key, value) => {
      map.set(key, value);
    },
    remove: (key) => {
      map.delete(key);
    },
  };
}

/**
 * The localStorage-backed store. Every operation is guarded: a browser that denies
 * storage (private mode, a quota-0 device, blocked third-party storage) or a render with no
 * `localStorage` at all (a prerender / node pass) degrades to an in-memory store, so a read or
 * write can never throw into a feature. Persistence is best-effort by design.
 */
export function localStorageStore(
  backing: Storage | undefined = globalThis.localStorage,
): KeyValueStore {
  if (!backing) return memoryStore();
  const fallback = memoryStore();
  return {
    get(key) {
      try {
        return backing.getItem(key);
      } catch {
        return fallback.get(key);
      }
    },
    set(key, value) {
      try {
        backing.setItem(key, value);
      } catch {
        fallback.set(key, value);
      }
    },
    remove(key) {
      try {
        backing.removeItem(key);
      } catch {
        fallback.remove(key);
      }
    },
  };
}

/**
 * Read a JSON value from the store, returning `fallback` when the key is absent, the stored
 * text is not valid JSON, or an optional validator rejects the parsed value. Persistence is
 * best-effort: a corrupt or stale blob never throws, it just falls back to the default. There
 * is no migration in v1 - nothing has ever been persisted - so a schema bump bumps the key.
 */
export function readJSON<T>(
  store: KeyValueStore,
  key: string,
  fallback: T,
  valid?: (value: unknown) => value is T,
): T {
  const raw = store.get(key);
  if (raw === null) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    // a predicate narrows the validated value to T, so the accepted path needs no cast; an
    // unvalidated read keeps the caller-asserts cast.
    if (valid) return valid(parsed) ? parsed : fallback;
    return parsed as T;
  } catch {
    return fallback;
  }
}

/** Write a JSON value to the store; serialization or quota failures are swallowed (best-effort). */
export function writeJSON<T>(store: KeyValueStore, key: string, value: T): void {
  try {
    store.set(key, JSON.stringify(value));
  } catch {
    // a cyclic value or a full quota is not fatal - the next visit simply restores the default
  }
}
